/*
 * Copyright (c) 2025. Bolmate B.V.
 */

export enum NotificationInterval {
    INSTANT = 'INSTANT',
    DAY = 'DAY',
    WEEK = 'WEEK',
    MONTH = 'MONTH',
    QUARTER = 'QUARTER',
    YEAR = 'YEAR'
}

export enum NotificationType {
    REVIEWS = 'REVIEWS',
    PRODUCT_HIJACK = 'PRODUCT_HIJACK',
    BUY_BOX = 'BUY_BOX',
    STOCK = 'STOCK',
    ORDER = 'ORDER'
}

export enum SubscriptionModuleName {
    NOTIFICATIONS = 'NOTIFICATIONS'
}

export enum SubscriptionModuleStatus {
    PAID = 'PAID',
    TRIAL = 'TRIAL',
    FREE = 'FREE',
    CANCELED = 'CANCELED',
    CANCELING = 'CANCELING'
}

export enum InvoiceOptionName {
    PREVIEW,
    EDIT,
    DOWNLOAD,
    DETAILS,
    SEND_INVOICE,
    UPLOAD_INVOICE,
    CREDIT,
    REVERSE_CHARGE,
    SET_NUMBER,
    CANCEL_EMAILS,
    COPY_ID,
    SEND_PEPPOL
}

export enum BuyBoxEvent {
    LOST = 'LOST',
    GOT = 'GOT',
    CHANGED = 'CHANGED'
}

export enum PeppolState {
    TRANSIT = 'TRANSIT',
    SENT = 'SENT',
    FAILED = 'FAILED'
}

export enum ProductSelectionEnum {
    ALL = 'ALL',
    ALL_EXCEPT = 'ALL_EXCEPT',
    REMAINING = 'REMAINING',
    SELECTED = 'SELECTED',
    NONE = 'NONE'
}

export enum SalesCategory {
    SALES = 'SALES',
    RETURNS = 'RETURNS',
    ADVERTISING = 'ADVERTISING',
    COSTS = 'COSTS',
    PROFIT = 'PROFIT',
    COMPARISONS = 'COMPARISONS'
}

export enum SalesColumn {
    TITLE = 'TITLE',
    QUANTITY = 'QUANTITY',
    REVENUE = 'REVENUE',
    ORGANIC_SALES = 'ORGANIC_SALES',
    RETURN_QUANTITY = 'RETURN_QUANTITY',
    RETURN_REVENUE = 'RETURN_REVENUE',
    RETURN_COSTS = 'RETURN_COSTS',
    RETURN_COMMISSION = 'RETURN_COMMISSION',
    AD_SPENT = 'AD_SPENT',
    SPONSORED_SALES = 'SPONSORED_SALES',
    ACOS = 'ACOS',
    CPC = 'CPC',
    PROD_COSTS = 'PROD_COSTS',
    COMMISSION = 'COMMISSION',
    VAT = 'VAT',
    MARGIN = 'MARGIN',
    PROFIT = 'PROFIT'
}

export enum PeriodLabel {
    TODAY = 'Vandaag',
    YESTERDAY = 'Gisteren',
    LAST_7_DAYS = 'Afgelopen 7 dagen',
    LAST_14_DAYS = 'Afgelopen 14 dagen',
    LAST_30_DAYS = 'Afgelopen 30 dagen',
    LAST_90_DAYS = 'Afgelopen 90 dagen',
    ALL = 'Alles',
    CUSTOM = 'Aangepast'
}

import type {TFunction} from 'i18next';

export function translatePeriodLabel(label: PeriodLabel, t: TFunction): string {
    switch (label) {
        case PeriodLabel.TODAY:
            return t('controls.periodSelector.labels.today', 'Vandaag');
        case PeriodLabel.YESTERDAY:
            return t('controls.periodSelector.labels.yesterday', 'Gisteren');
        case PeriodLabel.LAST_7_DAYS:
            return t('controls.periodSelector.labels.last7Days', 'Afgelopen 7 dagen');
        case PeriodLabel.LAST_14_DAYS:
            return t('controls.periodSelector.labels.last14Days', 'Afgelopen 14 dagen');
        case PeriodLabel.LAST_30_DAYS:
            return t('controls.periodSelector.labels.last30Days', 'Afgelopen 30 dagen');
        case PeriodLabel.LAST_90_DAYS:
            return t('controls.periodSelector.labels.last90Days', 'Afgelopen 90 dagen');
        case PeriodLabel.ALL:
            return t('controls.periodSelector.labels.all', 'Alles');
        case PeriodLabel.CUSTOM:
            return t('controls.periodSelector.labels.custom', 'Aangepast');
        default:
            return label;
    }
}

export enum FilterOperator {
    EQUALS = 'EQUALS',
    NOT_EQUALS = 'NOT_EQUALS',
    GT = 'GT',
    GTE = 'GTE',
    LT = 'LT',
    LTE = 'LTE',
    BETWEEN = 'BETWEEN'
}

export enum LVBSize {
    XXXS = 'XXXS',
    XXS = 'XXS',
    XS = 'XS',
    S = 'S',
    M = 'M',
    L = 'L',
    XL = 'XL',
    XXL = 'XXL',
    XXXL_1 = 'XXXL_1',
    XXXL_2 = 'XXXL_2'
}

export enum SalesProductView {
    STATS,
    EDIT_PRODUCT
}

export enum RankPositionFilter {
    ALL = 'ALL',
    TOP_1 = 'TOP_1',
    TOP_3 = 'TOP_3',
    TOP_5 = 'TOP_5',
    TOP_10 = 'TOP_10',
    TOP_30 = 'TOP_30',
    BOTTOM_100 = 'BOTTOM_100'
}

export enum APIType {
    RETAILER = 'RETAILER',
    ADVERTISER = 'ADVERTISER'
}

export enum SearchRankingSort {
    PRODUCT = 'PRODUCT',
    SEARCH_VOLUME = 'SEARCH_VOLUME',
    KEYWORDS = 'KEYWORDS',
    CATEGORIES = 'CATEGORIES',
    RANK_TODAY = 'RANK_TODAY',
    RANK_WEEK = 'RANK_WEEK',
    RANK_MONTH = 'RANK_MONTH',
    RANK_QUARTER = 'RANK_QUARTER',
    REVIEW_SCORE = 'REVIEW_SCORE',
    REVIEW_COUNT = 'REVIEW_COUNT',
    NPS = 'NPS',
    WINNING_BID = 'WINNING_BID',
    BUY_BOX = 'BUY_BOX'
}
